import { CSSProperties, useMemo } from 'react';
import { VStack } from '@cbhq/cds-web/layout/VStack';

import { MOBILE_WEB_SPACING_VERTICAL } from '../../constants';
import { ScreenHeader } from '../ScreenHeader';

import { IdentityMultiContentLayoutProps, LayoutVariantConfig } from './types';

type Props = IdentityMultiContentLayoutProps & { config: LayoutVariantConfig };

const contentWrapperStyle = { backgroundColor: '#0000001a' } as CSSProperties;
const contentHeaderStyle = { backgroundColor: '#0000001a' } as CSSProperties;
const contentFooterStyle = { backgroundColor: '#0000001a' } as CSSProperties;
const bodyStyle = { backgroundColor: '#0000001a' } as CSSProperties;
const endWrapperStyle = { marginTop: 'auto' } as CSSProperties;

export function IdentityMultiContentLayoutBase({
  title,
  description,
  picName,
  body,
  footer,
  name,
  start,
  end,
  footerTestId,
  isStorybookPrototype,
  testID,
  bodyTestID,
  legalElement,
  customWrapperStyle,
  footerLayout,
  config,
}: Props) {
  const isStacked = footerLayout === 'stacked';
  const isStackedBottom = footerLayout === 'stackedBottom';

  const {
    contentGap,
    footerGap,
    stackedPaddingTop,
    stackedWidth,
    bottomStackedGap,
    centerContent,
    pushFooterToBottom,
  } = config;

  const rootWrapperStyle = useMemo(
    () => ({
      ...(isStorybookPrototype ? contentWrapperStyle : undefined),
      ...customWrapperStyle,
    }),
    [isStorybookPrototype, customWrapperStyle],
  );

  // Both variants use mobile spacing for the content bottom padding.
  const contentWrapperSpacingBottom = useMemo(
    () => (footer ? MOBILE_WEB_SPACING_VERTICAL : undefined),
    [footer],
  );

  const footerComponent = useMemo(() => {
    if (!footer) return undefined;
    return (
      <VStack
        gap={footerGap}
        testID={footerTestId}
        style={isStorybookPrototype ? contentFooterStyle : undefined}
      >
        {footer}
      </VStack>
    );
  }, [footer, footerGap, footerTestId, isStorybookPrototype]);

  const bottomEndComponents = useMemo(() => {
    if (isStacked) {
      return (
        <VStack
          paddingTop={stackedPaddingTop}
          width={stackedWidth}
          testID="stacked-components"
          alignSelf="center"
        >
          {footerComponent}
          {end}
        </VStack>
      );
    }

    if (isStackedBottom) {
      return (
        <VStack
          style={endWrapperStyle}
          gap={bottomStackedGap}
          width={stackedWidth}
          testID="bottom-end-components"
          alignSelf="center"
        >
          {footerComponent}
          {end}
        </VStack>
      );
    }

    return (
      <>
        {footerComponent}
        {end}
      </>
    );
  }, [
    isStacked,
    isStackedBottom,
    stackedPaddingTop,
    stackedWidth,
    bottomStackedGap,
    footerComponent,
    end,
  ]);

  return (
    <VStack
      width="100%"
      flexGrow={1}
      style={rootWrapperStyle}
      testID={testID || `identity-multi-content-layout-${name}`}
      justifyContent={pushFooterToBottom && !isStacked ? 'space-between' : undefined}
    >
      <VStack
        gap={contentGap}
        paddingBottom={contentWrapperSpacingBottom}
        style={isStorybookPrototype ? contentWrapperStyle : undefined}
        testID={`identity-multi-content-layout-content-wrapper-${name}`}
      >
        {start}

        {(picName || title || description) && (
          <VStack
            gap={contentGap}
            style={isStorybookPrototype ? contentHeaderStyle : undefined}
            testID={`identity-multi-content-layout-content-header-wrapper-${name}`}
            alignItems={centerContent ? 'center' : undefined}
          >
            <ScreenHeader
              picName={picName}
              title={title}
              description={description}
              isStorybookPrototype={isStorybookPrototype}
              centerContent={centerContent}
            />
          </VStack>
        )}

        {body && (
          <VStack
            testID={bodyTestID || `identity-multi-content-layout-body-${name}`}
            style={isStorybookPrototype ? bodyStyle : undefined}
          >
            {body}
          </VStack>
        )}
      </VStack>

      {legalElement && <VStack paddingBottom={3}>{legalElement}</VStack>}

      {bottomEndComponents}
    </VStack>
  );
}
